import { FunctionComponent, useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  useToast,
  ToastType,
} from "../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../context_providers/modal/loader";
import AsyncSearchSelect from "../../../ui/new_components/common/async_search_select/AsyncSearchSelect";
import {
  addCommentApi,
  applyApi,
  applyPreviewApi,
  approveRequestApi,
  assignableDevsApi,
  assignRequestApi,
  getRequestDetailApi,
  rejectRequestApi,
  updateStatusApi,
} from "../service/request_desk_repository";
import { useRequestDeskAccess } from "../hooks/use_request_desk_access";
import ApplyPreviewPanel from "./ApplyPreviewPanel";
import {
  ApplyOptions,
  ApplyPreviewResult,
  AssignableDev,
  btnGreen,
  btnPrimary,
  btnRed,
  btnSecondary,
  fieldLabel,
  isForbiddenRes,
  RD,
  RequestDeskRequest,
  RequestDeskStatus,
  RequestDeskType,
  statusStyle,
  STATUS_LABEL,
  TYPE_LABEL,
} from "../models/types";

// "a, b\nc" -> ["a","b","c"] — plain-text id entry for v1 (release/allocate
// device ids), no picker yet.
const parseIdList = (text: string): string[] =>
  text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

const Card: FunctionComponent<{
  title?: string;
  children: any;
  extra?: any;
}> = ({ title, children, extra }) => (
  <div className="bg-white rounded-lg shadow p-4 mb-3">
    {title && (
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-semibold text-sm">{title}</h3>
        {extra}
      </div>
    )}
    {children}
  </div>
);

const RequestDeskDetailPage: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToast();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RequestDeskRequest | null>(null);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [rejectReason, setRejectReason] = useState("");
  const [resolutionNote, setResolutionNote] = useState("");
  const [comment, setComment] = useState("");
  const [denied, setDenied] = useState(false);
  const [dev, setDev] = useState<AssignableDev | null>(null);

  // ERP-176 Phase 2 — automated apply-preview/apply.
  const [preview, setPreview] = useState<ApplyPreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [ack, setAck] = useState(false);
  const [cascadePr, setCascadePr] = useState(false);
  const [compDirection, setCompDirection] = useState<"Inward" | "Outward">(
    "Inward",
  );
  const [compQty, setCompQty] = useState("");
  const [releaseIds, setReleaseIds] = useState("");
  const [allocateIds, setAllocateIds] = useState("");

  const access = useRequestDeskAccess();

  const buildApplyOptions = (): ApplyOptions => ({
    cascade_pr: cascadePr,
    compensating_movement: compQty.trim()
      ? { direction: compDirection, qty: Number(compQty) }
      : undefined,
    release_device_ids: releaseIds.trim() ? parseIdList(releaseIds) : undefined,
    allocate_device_ids: allocateIds.trim()
      ? parseIdList(allocateIds)
      : undefined,
  });

  // Any option change invalidates the current preview — Apply must always
  // run against a preview computed with the exact options it will submit.
  const invalidatePreview = () => {
    setPreview(null);
    setAck(false);
  };

  const load = async () => {
    setLoading(true);
    const res = await getRequestDetailApi(id);
    setLoading(false);
    if (res.success) {
      setDenied(false);
      setData(res.data);
      const tl = res.timeline;
      setTimeline(Array.isArray(tl) ? tl : (tl?.logs ?? tl?.data ?? []));
    } else if (isForbiddenRes(res)) {
      setDenied(true);
    } else {
      showToast({ type: ToastType.error, text: res.error || "Failed to load" });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (id) load();
  }, [id]);

  const run = async (fn: () => Promise<any>, okText: string) => {
    setLoading(true);
    const res = await fn();
    setLoading(false);
    if (res.success) {
      showToast({ type: ToastType.success, text: okText });
      load();
    } else {
      showToast({ type: ToastType.error, text: res.error || "Action failed" });
    }
  };

  const doPreview = async () => {
    setPreviewLoading(true);
    setAck(false);
    const res = await applyPreviewApi(id, buildApplyOptions());
    setPreviewLoading(false);
    if (res.success) {
      setPreview(res.data);
    } else {
      setPreview(null);
      showToast({
        type: ToastType.error,
        text: res.error || "Preview failed",
      });
    }
  };

  const doApply = async () => {
    setApplyLoading(true);
    const res = await applyApi(id, buildApplyOptions());
    setApplyLoading(false);
    if (res.success) {
      showToast({
        type: ToastType.success,
        text: res.data?.resolution_note || "Applied and resolved",
      });
      setPreview(null);
      setAck(false);
      load();
    } else {
      // The apply endpoint re-runs the planner fresh — if state drifted since
      // preview, it refuses and we surface why rather than pretending it applied.
      showToast({ type: ToastType.error, text: res.error || "Apply failed" });
      if (res.issues) {
        setPreview((p) =>
          p ? { ...p, ok: false, blocking_errors: res.issues } : p,
        );
      }
    }
  };

  if (denied) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="text-sm font-semibold mb-1">Access denied</div>
          <div className="text-xs text-gray-500">
            You do not have permission to view this request.
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-4">
        {loading && <LoadingWidget />}
        <div className="text-gray-400">Loading…</div>
      </div>
    );
  }

  const sr = data.service_request;
  const isSR = data.type === RequestDeskType.service_request;
  const parties = sr?.responsible_parties ?? [];
  const canAssign = access.canAssign(data);
  const canExecuteThis = access.canExecuteOn(data);
  const canApplyThis = access.canApplyOn(data);
  const isApproved = data.approval_status === "approved";
  const applyEntityType = sr?.entity_type;
  const applyField = sr?.change_requested?.field;

  return (
    <div className="p-4">
      {loading && <LoadingWidget />}

      <button
        className="text-sm font-semibold mb-2"
        style={{ color: RD.accent }}
        onClick={() => history.goBack()}
      >
        {"< Back"}
      </button>

      <Card>
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-base font-semibold">
              {data.request_no} · {data.title}
            </h2>
            <div className="text-xs text-gray-500 mt-1">
              {TYPE_LABEL[data.type]}{" "}
              {sr?.entity_ref ? `· ${sr.entity_ref}` : ""} · Raised by{" "}
              {data.created_by_name || "—"}
            </div>
          </div>
          <span
            className="px-3 py-1 rounded-full text-xs font-semibold"
            style={statusStyle(data.status)}
          >
            {STATUS_LABEL[data.status] || data.status}
          </span>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* LEFT */}
        <div className="lg:col-span-2">
          {isSR && sr && (
            <Card title="Requested change">
              {/* "Other" requests carry no field-level change — only the note. */}
              {sr.change_requested?.field && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
                  <b>{fieldLabel(sr.change_requested.field)}</b>
                  {sr.change_requested.line_ref
                    ? ` · ${sr.change_requested.line_ref}`
                    : ""}
                  <br />
                  <span className="font-mono bg-gray-100 px-1 rounded">
                    {sr.change_requested.current_value || "—"}
                  </span>
                  <span className="text-red-500 font-bold"> → </span>
                  <span className="font-mono bg-gray-100 px-1 rounded">
                    {sr.change_requested.desired_value || "—"}
                  </span>
                </div>
              )}
              <p className="text-sm text-gray-600 mt-2">{sr.change_note}</p>
              <p className="text-xs mt-2">
                <b>Reason:</b> {sr.reason_for_error}
                {sr.reason_detail ? ` — ${sr.reason_detail}` : ""} &nbsp;{" "}
                <b>Stage:</b>{" "}
                <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                  {sr.entity_stage || "—"}
                </span>
              </p>
            </Card>
          )}

          <Card title="Description">
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {data.description}
            </p>
            {data.bug && (
              <div className="text-xs text-gray-600 mt-2 space-y-1">
                <div>
                  <b>Module:</b> {data.bug.module}
                </div>
                <div>
                  <b>Steps:</b> {data.bug.steps}
                </div>
                <div>
                  <b>Expected:</b> {data.bug.expected}
                </div>
                <div>
                  <b>Actual:</b> {data.bug.actual}
                </div>
              </div>
            )}
            {data.attachments && data.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {data.attachments.map((a, i) => (
                  <a
                    key={i}
                    href={a}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-blue-600 underline"
                  >
                    Attachment {i + 1}
                  </a>
                ))}
              </div>
            )}
          </Card>

          {/* dev execution — assigned developer only, and only once approved */}
          {access.canExecute && !canExecuteThis && (
            <Card title="Dev execution">
              <p className="text-xs text-gray-500">
                {!isApproved
                  ? "Waiting for approval — execution opens once an approver clears this request."
                  : !data.assigned_to
                    ? "Not assigned yet — an approver must assign a developer."
                    : `Assigned to ${data.assigned_to_name || data.assigned_to}. Only the assigned developer can update execution.`}
              </p>
            </Card>
          )}

          {canExecuteThis && (
            <Card title="Dev execution">
              {/* Card only renders for the assigned dev on an approved request,
                  so any pre-start status can begin work (incl. legacy rows). */}
              {data.status !== RequestDeskStatus.in_progress && (
                <button
                  className="rounded px-3 py-1.5 text-sm font-semibold"
                  style={btnPrimary}
                  onClick={() =>
                    run(
                      () =>
                        updateStatusApi(id, {
                          status: RequestDeskStatus.in_progress,
                        }),
                      "Marked in progress",
                    )
                  }
                >
                  Start execution
                </button>
              )}
              {data.status === RequestDeskStatus.in_progress &&
                canApplyThis && (
                  <div
                    className="rounded-lg p-3 mb-3"
                    style={{
                      border: `1px solid ${RD.accentLine}`,
                      backgroundColor: RD.accentSoft,
                    }}
                  >
                    <div className="text-xs font-semibold mb-1">
                      Apply this change — {fieldLabel(applyField)}
                    </div>
                    <p className="text-xs text-gray-500 mb-2">
                      See what will happen before you apply this change. If it
                      can't be applied automatically, use manual resolution
                      below instead.
                    </p>

                    {/* entity-specific apply-time inputs — simple fields for v1,
                      no device/PR pickers yet */}
                    {applyEntityType === "PO" && (
                      <label className="flex items-center gap-2 text-xs mb-2">
                        <input
                          type="checkbox"
                          checked={cascadePr}
                          onChange={(e) => {
                            setCascadePr(e.target.checked);
                            invalidatePreview();
                          }}
                        />
                        Also update the linked Payment Request's amount to match
                        (it will need approval again)
                      </label>
                    )}
                    {applyEntityType === "SO" && (
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <select
                          className="p-2 border rounded text-xs"
                          value={compDirection}
                          onChange={(e) => {
                            setCompDirection(
                              e.target.value as "Inward" | "Outward",
                            );
                            invalidatePreview();
                          }}
                        >
                          <option value="Inward">Add to stock</option>
                          <option value="Outward">Remove from stock</option>
                        </select>
                        <input
                          className="p-2 border rounded text-xs"
                          placeholder="Stock correction quantity (raw materials only, optional)"
                          value={compQty}
                          onChange={(e) => {
                            setCompQty(e.target.value);
                            invalidatePreview();
                          }}
                        />
                      </div>
                    )}
                    {applyEntityType === "ST" && (
                      <div className="grid grid-cols-1 gap-2 mb-2">
                        <input
                          className="p-2 border rounded text-xs"
                          placeholder="Device IDs to release — only needed if you're reducing quantity"
                          value={releaseIds}
                          onChange={(e) => {
                            setReleaseIds(e.target.value);
                            invalidatePreview();
                          }}
                        />
                        <input
                          className="p-2 border rounded text-xs"
                          placeholder="Device IDs to add — only needed if you're increasing quantity"
                          value={allocateIds}
                          onChange={(e) => {
                            setAllocateIds(e.target.value);
                            invalidatePreview();
                          }}
                        />
                      </div>
                    )}

                    <button
                      className="rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-40 mb-2"
                      style={btnSecondary}
                      disabled={previewLoading}
                      onClick={doPreview}
                    >
                      {previewLoading ? "Checking…" : "Preview this change"}
                    </button>

                    {preview && (
                      <div className="mb-2">
                        <ApplyPreviewPanel preview={preview} />
                      </div>
                    )}

                    {preview && preview.ok && preview.warnings.length > 0 && (
                      <label className="flex items-center gap-2 text-xs mb-2">
                        <input
                          type="checkbox"
                          checked={ack}
                          onChange={(e) => setAck(e.target.checked)}
                        />
                        I understand the above and want to continue
                      </label>
                    )}

                    {preview && preview.ok && (
                      <button
                        className="rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-40"
                        style={btnGreen}
                        disabled={
                          applyLoading || (preview.warnings.length > 0 && !ack)
                        }
                        onClick={doApply}
                      >
                        {applyLoading ? "Applying…" : "Apply change"}
                      </button>
                    )}
                  </div>
                )}

              {data.status === RequestDeskStatus.in_progress && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 mb-1">
                    {canApplyThis
                      ? "Manual resolution (if this change can't be applied automatically)"
                      : "Manual resolution"}
                  </div>
                  <textarea
                    className="p-2 border rounded w-full text-sm mb-2"
                    rows={3}
                    placeholder="Describe what you changed and why — required"
                    value={resolutionNote}
                    onChange={(e) => setResolutionNote(e.target.value)}
                  />
                  <button
                    className="rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-40"
                    style={btnGreen}
                    disabled={!resolutionNote.trim()}
                    onClick={() =>
                      run(
                        () =>
                          updateStatusApi(id, {
                            status: RequestDeskStatus.resolved,
                            resolution_note: resolutionNote,
                          }),
                        "Resolved",
                      )
                    }
                  >
                    Mark Resolved
                  </button>
                </div>
              )}
            </Card>
          )}

          {data.resolution_note && (
            <Card title="Resolution note">
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {data.resolution_note}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                by {data.resolved_by_name}
              </p>
            </Card>
          )}

          {/* comments */}
          <Card title="Comments">
            <div className="space-y-2 mb-2">
              {(data.comments ?? []).map((c, i) => (
                <div key={i} className="text-sm border-b pb-1">
                  <span className="font-semibold">{c.by_name}</span>
                  <span className="text-gray-400 text-xs ml-2">
                    {c.at ? new Date(c.at).toLocaleString() : ""}
                  </span>
                  <div className="text-gray-600">{c.text}</div>
                </div>
              ))}
              {(data.comments ?? []).length === 0 && (
                <div className="text-xs text-gray-400">No comments yet</div>
              )}
            </div>
            <div className="flex gap-2">
              <input
                className="p-2 border rounded flex-1 text-sm"
                placeholder="Add a comment…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <button
                className="bg-white border rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-40"
                disabled={!comment.trim()}
                onClick={() =>
                  run(async () => {
                    const r = await addCommentApi(id, comment);
                    if (r.success) setComment("");
                    return r;
                  }, "Comment added")
                }
              >
                Post
              </button>
            </div>
          </Card>
        </div>

        {/* RIGHT */}
        <div>
          {isSR && (
            <Card
              title="Responsible parties"
              extra={
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                  snapshot @ raise
                </span>
              }
            >
              <div className="flex flex-col gap-2">
                {parties.length === 0 && (
                  <span className="text-xs text-gray-400">None captured</span>
                )}
                {parties.map((p, i) => (
                  <span
                    key={i}
                    className="bg-white border rounded-full px-2 py-1 text-xs"
                  >
                    <b>{p.name || p.uid}</b> · {p.role}
                  </span>
                ))}
              </div>
            </Card>
          )}

          {/* approval — every request type needs it before dev work can start */}
          {access.canApproveOn(data) && (
            <Card
              title="Approval"
              extra={
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                  action required
                </span>
              }
            >
              <p className="text-xs text-gray-500 mb-2">
                {isSR
                  ? "Approving confirms this is a genuine data error (notifies responsible parties + HODs)."
                  : "Approving releases this request to the dev queue for assignment."}
              </p>
              <div className="flex gap-2 mb-2">
                <button
                  className="rounded px-3 py-1.5 text-sm font-semibold"
                  style={btnGreen}
                  onClick={() => run(() => approveRequestApi(id), "Approved")}
                >
                  ✓ Approve
                </button>
              </div>
              <input
                className="p-2 border rounded w-full text-sm mb-2"
                placeholder="Rejection reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              <button
                className="rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-40"
                style={btnRed}
                disabled={!rejectReason.trim()}
                onClick={() =>
                  run(() => rejectRequestApi(id, rejectReason), "Rejected")
                }
              >
                ✗ Reject
              </button>
            </Card>
          )}

          {/* assignment — approver only, and only on an approved request */}
          {access.canTriage && (
            <Card title="Assignment">
              <p className="text-xs text-gray-500 mb-2">
                Assigned to: {data.assigned_to_name || "—"}
              </p>
              {canAssign ? (
                <>
                  <AsyncSearchSelect<AssignableDev>
                    value={dev}
                    onSelect={setDev}
                    minChars={0}
                    size="md"
                    placeholder="Search developer…"
                    fetcher={async (q) => {
                      const res = await assignableDevsApi(q);
                      return res.success ? (res.data ?? []) : [];
                    }}
                    getOptionLabel={(d) => d?.name ?? ""}
                    getOptionKey={(d) => d?.uid ?? ""}
                    renderOption={(d) => (
                      <div className="text-xs">
                        <b>{d.name}</b>
                        {d.department ? (
                          <span className="text-gray-400">
                            {" "}
                            · {d.department}
                          </span>
                        ) : null}
                      </div>
                    )}
                  />
                  <button
                    className="bg-white border rounded px-3 py-1.5 text-sm font-semibold mt-2 disabled:opacity-40"
                    disabled={!dev}
                    onClick={() =>
                      run(
                        () =>
                          assignRequestApi(id, {
                            assigned_to: String(dev?.uid ?? ""),
                          }),
                        `Assigned to ${dev?.name ?? ""}`,
                      )
                    }
                  >
                    {data.assigned_to ? "Reassign" : "Assign"}
                  </button>
                </>
              ) : (
                <p className="text-xs text-gray-400">
                  {!isApproved
                    ? "Assignment opens once this request is approved."
                    : "This request is closed — assignment is locked."}
                </p>
              )}
            </Card>
          )}

          {/* timeline */}
          <Card title="Timeline">
            <ul className="space-y-2">
              {timeline.length === 0 && (
                <li className="text-xs text-gray-400">No activity yet</li>
              )}
              {timeline.map((t, i) => (
                <li key={i} className="text-xs border-l-2 border-gray-200 pl-2">
                  <div className="font-semibold">{t.operation}</div>
                  <div className="text-gray-400">
                    {t.created_at
                      ? new Date(t.created_at).toLocaleString()
                      : ""}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RequestDeskDetailPage;
